import { toast } from 'react-toastify';
import { captureError, captureMessage } from './sentry';

// Error messages for production
const PRODUCTION_ERROR_MESSAGES = {
  network: 'Unable to connect. Please check your internet connection.',
  default: 'An unexpected error occurred. Please try again later.',
  auth: 'Authentication error. Please try again.',
  notFound: 'The requested resource was not found.',
  validation: 'Invalid input. Please check your data.',
  server: 'Service temporarily unavailable. Please try again later.'
};

// Error messages for development
const DEVELOPMENT_ERROR_MESSAGES = {
  network: 'Network Error: Unable to reach the server',
  default: 'Error occurred: Check console for details',
  auth: 'Authentication failed: Invalid credentials or token',
  notFound: '404: Resource not found',
  validation: 'Validation Error: Invalid input data',
  server: 'Server Error: Internal error occurred'
};

export const handleApiError = (error, component = 'unknown') => {
  const isProd = process.env.NODE_ENV === 'production';
  const messages = isProd ? PRODUCTION_ERROR_MESSAGES : DEVELOPMENT_ERROR_MESSAGES;

  // Log errors only in development
  if (!isProd) {
    console.error('API Error:', error);
  }

  // Prepare sanitized context for Sentry
  const context = {
    component,
    feature: 'api',
    url: error.config?.url?.replace(/\/[0-9]+/g, '/:id'), // Sanitize URLs with IDs
    method: error.config?.method,
    status: error.response?.status,
    statusText: error.response?.statusText,
    // Don't include response data in production
    ...(isProd ? {} : { data: error.response?.data })
  };

  // Handle specific error types
  if (!error.response) {
    toast.error(messages.network);
    captureError(error, { ...context, errorType: 'network' });
    return;
  }

  const status = error.response.status;
  switch (status) {
    case 401:
    case 403:
      toast.error(messages.auth);
      break;
    case 404:
      toast.error(messages.notFound);
      break;
    case 422:
      toast.error(messages.validation);
      break;
    case 500:
      toast.error(messages.server);
      break;
    default:
      toast.error(messages.default);
  }

  captureError(error, { ...context, errorType: `http_${status}` });
};

export const handleError = (error, component = 'unknown', feature = 'unknown') => {
  const isProd = process.env.NODE_ENV === 'production';

  // Prepare context
  const context = {
    component,
    feature,
    errorType: error.name,
    errorMessage: error.message,
  };

  // Only log in development
  if (!isProd) {
    console.error(`Error in ${component}:`, error);
  }

  // Capture in Sentry
  captureError(error, context);

  // Show user-friendly message
  toast.error(
    isProd
      ? PRODUCTION_ERROR_MESSAGES.default
      : `${component}: ${error.message || DEVELOPMENT_ERROR_MESSAGES.default}`
  );
};
